import React, {useState, useEffect, useMemo} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit, faTrash, faPlus} from "@fortawesome/free-solid-svg-icons";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {FormProvider, useForm} from "react-hook-form";
import dayjs from "dayjs";

// Contexts
import {useToast} from "../../contexts/toast.js";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";

// Utils
import {fromMilitary, generateUniqueKey} from "../../utils/helpers.js";
import {replacePseudoFields} from "../../utils/builder.js";
import {getWeatherDependsLabel, getWeatherUnits, isWeatherConditionDepends} from "./helpers.js";

// Components
import Modal from "../../components/Modal.js";
import ManageConditional from "./ManageConditional.js";
import {InputRadioGroup} from "../../components/form/FormInputs.js";

// Style
import {flex, z} from "../../style/components/mixins.js";
import {border, pad, radius} from "../../style/components/variables.js";
import {voice} from "../../style/components/typography.js";
import {bp} from "../../style/components/breakpoints.js";
import {
  Button,
  ButtonFull,
  Error,
  Form,
  FormField,
  FormGroup,
  HeadingCenter,
  Inline,
  Label,
  Text
} from "../../style/components/general.js";

const checkMap = {
  "===": "is",
  "!==": "is not"
};

const compareMap = {
  checked: "has all checked",
  unchecked: "has all unchecked",
  one: "has one checked",
  more: "has at least one checked"
};

const ModalConditionals = ({
  visible = false,
  setVisible,
  targetElement,
  fields,
  save,
  builder,
  hasBackButton = false,
  goBack,
  hasPrimaryAddress = false,
  bulk = false
}) => {
  const isMounted = useMountedState();
  const {addToast} = useToast();

  const [targetCondition, setTargetCondition] = useState(null);
  const [targetConditionIdx, setTargetConditionIdx] = useState(null);
  const [openManage, setOpenManage] = useState(!targetElement?.condition?.list?.length);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);

  const schema = yup.object().shape({
    conditions: yup.array().test({
      test: val => !bulk || val?.length > 0,
      message: "Please define at least one condition."
    }),
    action: yup.string().required("Required")
  });
  const defaultValues = {
    conditions: [],
    action: targetElement?.condition ? targetElement.condition.action : "Show as a REQUIRED field",
    operator: targetElement?.condition?.operator || "or"
  };

  const form = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(schema)
  });
  const {
    watch,
    handleSubmit,
    setValue,
    formState: {errors}
  } = form;
  const watchConditions = watch("conditions");
  const watchAction = watch("action");
  const watchOperator = watch("operator");

  useEffect(() => {
    if (!visible) {
      setValue("conditions", []);
      setValue("action", "Show as a REQUIRED field");
      setOpenManage(false);
      setShowUnsavedWarning(false);
      setTargetCondition(null);
      setTargetConditionIdx(null);
    } else {
      const list = targetElement?.condition?.list || [];
      setValue("conditions", list);
      setValue(
        "action",
        targetElement?.condition ? targetElement.condition.action : "Show as a REQUIRED field"
      );
      setOpenManage(!list.length);
      setTargetCondition(null);
      setTargetConditionIdx(null);
    }
  }, [setValue, targetElement, visible]);

  useEffect(() => {
    if (isMounted() && targetElement?.condition?.list)
      setValue("conditions", targetElement.condition.list);
  }, [isMounted, targetElement, setValue]);

  useEffect(() => {
    if (!openManage) setShowUnsavedWarning(false);
  }, [openManage]);

  const handleDelete = idx => {
    const tempConditions = [...watchConditions];
    tempConditions.splice(idx, 1);
    setValue("conditions", tempConditions);
  };

  const showConflictWarning = useMemo(
    () => bulk && fields?.some(field => !!builder.byId[field]?.condition?.list?.length),
    [bulk, fields, builder]
  );

  const submission = values => {
    const tempConditions = [...watchConditions];
    const {trigger, depends: currentDepends, conditionArray: arr, operator: condOperator} = values;

    const toSave = {
      trigger,
      depends: currentDepends,
      check:
        currentDepends === "weekday" || currentDepends === "date"
          ? null
          : arr.map(cond => cond.check),
      compare:
        currentDepends === "weekday"
          ? arr[0].compare
          : arr.map(cond => {
              let {compare} = cond;
              if (cond?.frequency) {
                compare += `_${cond.frequency}`;
                if (cond?.lastDay) compare += "_last";
              }
              return compare;
            }),
      operator: condOperator
    };

    if (
      tempConditions &&
      tempConditions.length > 0 &&
      (targetConditionIdx || targetConditionIdx === 0)
    )
      tempConditions.splice(targetConditionIdx, 1, toSave);
    else tempConditions.push(toSave);

    setValue("conditions", tempConditions);
    setTargetCondition(null);
    setTargetConditionIdx(null);
    setOpenManage(false);
  };

  const handleEdit = (condition, i) => {
    const {trigger, depends: currDepends, check, compare, operator: condOperator} = condition;
    let conditionArray = [];

    let triggerDefault = trigger;

    if (!triggerDefault)
      triggerDefault = currDepends !== "weekday" && currDepends !== "date" ? "field" : currDepends;

    if (currDepends !== "weekday" && Array.isArray(compare))
      compare.map((compareVal, compareIdx) => {
        if (check)
          conditionArray.push({check: check[compareIdx] ?? null, compare: compareVal ?? null});
        else {
          const parts = compareVal.split("_");
          conditionArray.push({
            check: null,
            compare: parts?.length >= 1 ? parts[0] : null,
            frequency: parts?.length >= 2 ? parts[1] : null,
            lastDay: parts?.length === 3
          });
        }
      });
    else if (compare) conditionArray = [{check: null, compare}];

    const target = {
      trigger: triggerDefault,
      condition: {depends: currDepends, operator: condOperator, conditionArray}
    };
    setTargetCondition(target);
    setTargetConditionIdx(i);
    setOpenManage(true);
  };

  const getOptions = () => {
    if (!targetElement)
      return [
        "Show as a REQUIRED field",
        "Show as an OPTIONAL field",
        "Make a REQUIRED field",
        "Make an OPTIONAL field",
        "Hide field"
      ];
    if (targetElement?.type === "confirm") return ["Show as a REQUIRED field", "Hide field"];
    return [
      "Show as a REQUIRED field",
      "Show as an OPTIONAL field",
      `Make ${targetElement?.required ? "an" : "a"} ${
        targetElement?.required ? "OPTIONAL" : "REQUIRED"
      } field`,
      "Hide field"
    ];
  };

  const saveConditionals = () => {
    if (showConflictWarning && !showConflictModal) {
      setShowConflictModal(true);
      return;
    }

    if (openManage) addToast("You have unsaved changes.", "error");
    else {
      if (!watchConditions || watchConditions.length === 0) save({condition: null});
      else
        save({
          condition: {action: watchAction, operator: watchOperator, list: watchConditions}
        });
      if (hasBackButton && goBack && !bulk) goBack();
      else setVisible(false);
    }
  };

  const getCompareVals = (compare, i, compareList, dep, condOperator, trigger) => {
    let transformed = compare;

    if (trigger === "parentDisabled") {
      // parent disabled conditions only have one possible item in compare array

      return compare === "disabled"
        ? builder.byId[dep]?.disabledLabel || "Out of Service"
        : builder.byId[dep]?.enabledLabel || "In Service";
    }

    if (dep === "date") {
      const parts = compare.split("_");
      if (parts?.length > 1) {
        transformed = `Recurring ${parts[1].toUpperCase()}`;
        transformed +=
          parts[1] === "monthly" && parts?.length === 3
            ? " on the last day of the month"
            : ` starting ${dayjs(parts[0].slice(5)).format("MMM D")}`;
      } else transformed = dayjs(compare.slice(5)).format("MMM D");
    }

    if (dep === "weekday" || dep === "date") {
      if (i < compareList.length - 2) return `${transformed}, `;
      if (i === compareList.length - 2 && compareList.length === 2) return `${transformed} or `;
      if (i === compareList.length - 2) return `${transformed}, or `;
      return `${transformed}.`;
    }

    if (builder.byId[dep]?.type === "time") return fromMilitary(`${transformed}`);

    transformed =
      transformed in compareMap ? compareMap[transformed] : transformed.replace("Other: ", " ");

    const units = isWeatherConditionDepends(dep) ? getWeatherUnits(dep) : "";
    const withUnits = units ? `${transformed} ${units}` : transformed;

    if (i < compareList.length - 2) return `${withUnits}, `;
    if (i === compareList.length - 2 && compareList.length === 2)
      return `${withUnits} ${condOperator} `;
    if (i === compareList.length - 2) return `${withUnits}, ${condOperator} `;
    return withUnits;
  };

  const getConditionLabel = (
    sanitizedDepends,
    suffix,
    compare,
    check,
    condOperator,
    trigger,
    idx
  ) => (
    <ConditionLabel data-testid="condition">
      {sanitizedDepends &&
        sanitizedDepends in builder.byId &&
        `${builder.byId[sanitizedDepends].label} ${suffix ? `${suffix} ` : ""}`}
      {sanitizedDepends === "weekday" && "Current Day is "}
      {isWeatherConditionDepends(sanitizedDepends) &&
        `${getWeatherDependsLabel(sanitizedDepends)} `}
      {compare &&
        Array.isArray(compare) &&
        compare.map((curr, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={`compare-${idx}-${i}`}>
            {/* if compare is in compare map, omit the check value as the 
                compare label includes the proper verbage */}
            {!(curr in compareMap) &&
              check &&
              check[i] &&
              `${check[i] in checkMap ? checkMap[check[i]] : check[i]} `}
            {getCompareVals(curr, i, compare, sanitizedDepends, condOperator, trigger)}
          </span>
        ))}
      {compare && !Array.isArray(compare) && (
        <span>
          {/* if compare is in compare map, omit the check value as the 
                compare label includes the proper verbage */}
          {!(compare in compareMap) && check && `${check in checkMap ? checkMap[check] : check} `}
          {getCompareVals(compare, 0, [compare], sanitizedDepends)}
        </span>
      )}
    </ConditionLabel>
  );

  if (showConflictModal)
    return (
      <Modal
        visible={showConflictModal && visible}
        setVisible={setVisible}
        hasBackButton
        goBack={() => setShowConflictModal(false)}>
        <HeadingCenter>Continue?</HeadingCenter>
        <br />
        <Text>
          <Warning strong>Warning!</Warning>&nbsp; One or more targeted fields already have a
          condition defined. If a condition exists for a given field, the newly created
          condition&apos;s subitems will simply be added to the existing condition. The action,
          however, will be overwritten by the one indicated here.
        </Text>
        <br />
        <Text>Please confirm that you would like to continue with this action.</Text>
        <br />
        <Inline>
          <Button
            type="button"
            onClick={() => {
              saveConditionals();
            }}
            data-testid="conditionals.confirm">
            Continue
          </Button>
          <Button
            type="button"
            onClick={() => setShowConflictModal(false)}
            data-testid="conditionals.cancel">
            Cancel
          </Button>
        </Inline>
      </Modal>
    );

  return (
    <Modal visible={visible} setVisible={setVisible} hasBackButton={hasBackButton} goBack={goBack}>
      <HeadingCenter>Apply{fields?.length ? " Bulk" : ""} Conditional</HeadingCenter>
      {targetElement && (
        <FormField>
          <Text bold>SELECTED ELEMENT: {targetElement?.label.toUpperCase()}</Text>
        </FormField>
      )}
      <LabelContainer>
        <Label bold>CONDITIONS</Label>
      </LabelContainer>

      {watchConditions?.map((condition, idx) => {
        const {depends: currDepends, compare, check, operator: condOperator, trigger} = condition;
        const {sanitized: sanitizedDepends, suffix} = replacePseudoFields(currDepends);
        if (idx !== targetConditionIdx)
          return (
            <WrapperInline key={`${generateUniqueKey(sanitizedDepends)}`}>
              {getConditionLabel(
                sanitizedDepends,
                suffix,
                compare,
                check,
                condOperator,
                trigger,
                idx
              )}
              <Inline>
                <Option
                  type="button"
                  title="Edit"
                  onClick={() => handleEdit(condition, idx)}
                  data-testid="condition.edit">
                  <FontAwesomeIcon icon={faEdit} />
                </Option>
                <Option
                  type="button"
                  title="Delete"
                  onClick={() => handleDelete(idx)}
                  data-testid="condition.delete">
                  <FontAwesomeIcon icon={faTrash} />
                </Option>
              </Inline>
            </WrapperInline>
          );

        if (targetCondition)
          return (
            openManage && (
              <Wrapper key={`${generateUniqueKey(sanitizedDepends)}`}>
                {getConditionLabel(
                  sanitizedDepends,
                  suffix,
                  compare,
                  check,
                  condOperator,
                  trigger,
                  idx
                )}
                <hr />
                <ManageConditional
                  builder={builder}
                  submission={submission}
                  existing={targetCondition}
                  setExisting={setTargetCondition}
                  open={state => {
                    setOpenManage(state);
                    setTargetConditionIdx(null);
                  }}
                  hasPrimaryAddress={hasPrimaryAddress}
                  targetElement={targetElement}
                  fields={fields}
                />
              </Wrapper>
            )
          );

        return null;
      })}

      {openManage && !targetCondition && (
        <Wrapper>
          <ManageConditional
            builder={builder}
            submission={submission}
            existing={targetCondition}
            setExisting={setTargetCondition}
            open={state => {
              setOpenManage(state);
              setTargetConditionIdx(null);
              setTargetCondition(null);
            }}
            targetElement={targetElement}
            hasPrimaryAddress={hasPrimaryAddress}
            fields={fields}
          />
        </Wrapper>
      )}

      {!openManage && (
        <Add data-testid="condition.add" type="button" onClick={() => setOpenManage(true)}>
          <FontAwesomeIcon icon={faPlus} />
          &nbsp;Condition
        </Add>
      )}

      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(saveConditionals)}>
          <FormGroup>
            {watchConditions?.length > 1 && (
              <FormField>
                <InputRadioGroup
                  name="operator"
                  label="Logical Operator"
                  options={[{option: "or"}, {option: "and"}]}
                />
              </FormField>
            )}
            <FormField>
              <InputRadioGroup
                name="action"
                label="Action"
                options={getOptions()}
                orient
                testId="action"
              />
            </FormField>
          </FormGroup>
          {errors?.conditions && <Error>{errors.conditions.message}</Error>}
          <br />
          {showConflictWarning && (
            <>
              <Text quiet>
                <Warning strong quiet>
                  Warning!
                </Warning>
                &nbsp; One or more targeted fields already have a condition defined. If a condition
                exists for a given field, the newly created condition&apos;s subitems will simply be
                added to the existing condition. The action, however, will be overwritten by the one
                indicated here.
              </Text>
              <br />
            </>
          )}
          <SubmitWrap
            data-testid="conditionals.submit-wrap"
            disabled={openManage ? 1 : 0}
            onClick={() => {
              if (openManage) setShowUnsavedWarning(true);
            }}>
            <ButtonFull type="submit" data-testid="conditionals.submit" disabled={openManage}>
              Update Field
            </ButtonFull>
            {openManage && showUnsavedWarning && (
              <Error data-testid="conditionals.unfinished">You have unsaved changes.</Error>
            )}
          </SubmitWrap>
        </Form>
      </FormProvider>
    </Modal>
  );
};

ModalConditionals.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  targetElement: PropTypes.objectOf(PropTypes.any),
  save: PropTypes.func.isRequired,
  builder: PropTypes.objectOf(PropTypes.any).isRequired,
  hasBackButton: PropTypes.bool,
  goBack: PropTypes.func,
  hasPrimaryAddress: PropTypes.bool,
  fields: PropTypes.arrayOf(PropTypes.string),
  bulk: PropTypes.bool
};

// Style Overrides
const Wrapper = styled.div`
  border: ${border} solid ${({theme}) => theme.primary};
  border-radius: ${radius};
  padding: ${pad}px ${pad}px ${pad * 1.5}px;
  width: 100%;
  margin-bottom: ${pad}px;

  ${Label} {
    margin: 0;
  }
`;

const WrapperInline = styled(Wrapper)`
  ${flex("row", "nowrap", "space-between", "center")};
`;

const Option = styled(Button)`
  font-weight: bold;
  z-index: ${z("above")};
  ${voice.normal}
`;

const ConditionLabel = styled(Label)`
  max-width: calc(100% - 100px);
`;

const Add = styled(Button)`
  margin-right: ${pad}px;
  padding-left: 11.5px;
  padding-right: 11.5px;
  margin-bottom: ${pad * 2}px;
  margin-left: ${pad / 2}px;

  ${bp(2)} {
    margin-left: 0;
  }
`;

const SubmitWrap = styled.div`
  width: 100%;

  ${({disabled}) =>
    disabled &&
    css`
      cursor: not-allowed;

      ${ButtonFull} {
        pointer-events: none;
      }
    `}
`;

const LabelContainer = styled.div`
  margin-bottom: ${pad}px;
`;

const Warning = styled.span`
  color: ${({theme}) => theme.warning};

  ${({quiet}) => quiet && voice.quiet}

  ${({strong}) =>
    strong &&
    css`
      font-weight: bold;
    `}
`;

export default ModalConditionals;
